import { useEffect, useRef, useState, type FormEvent } from "react";
import { isLegacyAutoProjectName } from "../domain/productWorkflow";
import { usePlannerStore } from "../store/usePlannerStore";
import { useCatalogCover } from "../store/useCatalogCover";
import { initialProject } from "../domain/project";
import type { PlannerProject } from "../types";
import "./EmployeeWorkspace.css";
import { CatalogPublisher } from "./CatalogPublisher";
import { PriceEditor, priceAttentionCount, type AdminPrices } from "./PriceEditor";
import { SiteEditor } from "./SiteEditor";
import { CatalogAdmin, type AdminCatalogModel } from "./CatalogAdmin";
import { OrdersAdmin, type OrderRequest } from "./OrdersAdmin";
import { api, ApiError } from "./workspaceApi";
import { isChestShelf, replaceWithCatalogComposition, replaceWithCatalogShelf } from "../domain/catalogImport";
import type { WardrobeModule } from "../types";

/** Права по галочкам (08.10): у администратора — все и ещё «Сотрудники». */
type Permission = "orders" | "catalog" | "prices" | "projects" | "site";
const PERMISSIONS: [Permission, string, string][] = [
  ["orders", "Заявки", "заявки клиентов, подтверждение, визуализации"],
  ["catalog", "Витрина", "публикация моделей и раздел «Витрина»"],
  ["prices", "Цены", "прайс сметы"],
  ["projects", "Все проекты", "проекты всех сотрудников, удаление из корзины навсегда"],
  ["site", "Сайт", "тексты, фото и «Наши работы» на сайте"]
];
type User = { id: string; login: string; name: string; role: "admin" | "employee"; active: number; permissions?: Permission[] };
const can = (user: User | null, permission: Permission) => !!user && (user.role === "admin" || !!user.permissions?.includes(permission));
const accessLabel = (user: User) => user.role === "admin" ? "Администратор"
  : PERMISSIONS.filter(([key]) => user.permissions?.includes(key)).map(([, title]) => title).join(", ") || "Свои проекты";
type Saved = { id: string; name: string; client: string; revision: number; document: PlannerProject };
type Card = Omit<Saved, "document"> & { ownerId: string; ownerName: string; updatedAt: string; deletedAt?: string | null; modules: { type: string; width: number; height: number }[];
  /** Есть ли картинка проекта (снимок 3D при сохранении, 10.10). */
  hasPreview?: boolean };
type AdminCatalogDetail = AdminCatalogModel & ({ module: WardrobeModule; modules?: undefined } | { modules: WardrobeModule[]; module?: undefined });
type Version = { revision: number; savedAt: string; authorName: string };
type Section = "projects" | "orders" | "catalog" | "staff" | "prices" | "site";
const SECTION_TITLES: Record<Section, { title: string; lead: string }> = {
  projects: { title: "Проекты и клиенты", lead: "Проекты всех сотрудников и текущая конструкция" },
  orders: { title: "Заявки", lead: "Заявки клиентов из конструктора: контакты, PDF, техкарта и визуализации" },
  catalog: { title: "Витрина", lead: "«Готовые изделия Egel»: модели Egel и опубликованные дизайнерами, в том числе скрытые" },
  prices: { title: "Прайс", lead: "Все цены сметы конструктора. После сохранения стоимость у клиентов пересчитывается сразу" },
  staff: { title: "Сотрудники", lead: "Доступ к кабинету и права: кому заявки, кому витрина, кому цены" },
  site: { title: "Сайт", lead: "Тексты, фото и контакты на сайте, «Наши работы». После сохранения страницы сайта обновляются сразу" }
};
const documentOf = (p: PlannerProject): PlannerProject => ({ ...p, selectedModuleId: null, selectedItemId: null, selectedShelvingCellId: null });
const fingerprint = (p: PlannerProject) => JSON.stringify(documentOf(p));
const date = (value: string) => new Date(value).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const backupKey = "egel-workspace-local-backup";
/** Ссылка из уведомления о заявке: /?kabinet=zayavki открывает кабинет на «Заявках». */
const ORDERS_LINK_PARAM = "kabinet";
function takeOrdersLink() {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get(ORDERS_LINK_PARAM) !== "zayavki") return false;
    url.searchParams.delete(ORDERS_LINK_PARAM);
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    return true;
  } catch { return false; }
}
const metadataKey = "egel-workspace-draft-metadata";
function localMetadata(user: User) {
  try {
    const m = JSON.parse(localStorage.getItem(metadataKey) || "null");
    return m?.userId === user.id && typeof m.name === "string" && typeof m.client === "string" ? m : null;
  } catch { return null; }
}

/** Картинка проекта на карточке: снимок 3D; у старых проектов, пока их не пересохранят, — схема. */
function ProjectCardImage({ card }: { card: Card }) {
  const [failed, setFailed] = useState(false);
  if (!card.hasPreview || failed) return <ProjectPreview modules={card.modules} />;
  return <img className="workspace-card-image" alt={`Проект «${card.name}»`} loading="lazy" onError={() => setFailed(true)}
    src={`/api/workspace/projects/${encodeURIComponent(card.id)}/preview?v=${encodeURIComponent(card.updatedAt)}`} />;
}

function ProjectPreview({ modules }: { modules: Card["modules"] }) {
  const total = modules.reduce((s, m) => s + m.width, 0) || 1;
  const max = Math.max(2400, ...modules.map(m => m.height));
  let offset = 8;
  return <svg viewBox="0 0 240 110" role="img" aria-label="Схема состава проекта">
    <path d="M8 101H232" stroke="#d2c7bb" />
    {modules.slice(0, 30).map((m, i) => {
      const width = 220 * m.width / total, height = 87 * m.height / max, x = offset; offset += width;
      return <g key={i} fill={m.type === "shelving" ? "#c5ad91" : "#dfd3c2"} stroke="#8c735b" strokeWidth="1">
        <rect x={x} y={100 - height} width={Math.max(1, width - 2)} height={height} />
        {m.type === "shelving" ? [1, 2, 3].map(n => <path key={n} d={`M${x} ${100 - height * n / 4}h${Math.max(1, width - 2)}`} />) : <path d={`M${x + width / 2} ${100 - height}v${height}`} />}
      </g>;
    })}
  </svg>;
}

export function EmployeeWorkspace({ onStatus, onOpen, onStaffChange, getPreview }: {
  onStatus: (status: string) => void;
  onOpen: () => void;
  /** Снимок 3D для карточки проекта; null — сцена ещё не готова. */
  getPreview?: () => string | null;
  /** Вошёл ли сотрудник: номер версии и «Обновить» в шапке видят только сотрудники (09.10, 1.1). */
  onStaffChange?: (staff: boolean) => void;
}) {
  const project = usePlannerStore(s => s.project);
  const [visible, setVisible] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [cards, setCards] = useState<Card[]>([]);
  const [trash, setTrash] = useState<Card[] | null>(null);
  const [catalogCount, setCatalogCount] = useState(0);
  const [ordersPending, setOrdersPending] = useState(0);
  /** Открыли по ссылке на заявки — после входа сразу раздел «Заявки». */
  const wantOrders = useRef(false);
  /** Модель витрины открыли для правки: в кабинете сразу видна её публикация. */
  const [publisherOpen, setPublisherOpen] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [section, setSection] = useState<Section>("projects");
  const [pricesDirty, setPricesDirty] = useState(false);
  const [siteDirty, setSiteDirty] = useState(false);
  const [priceAttention, setPriceAttention] = useState(0);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState<Saved | null>(null);
  const [name, setName] = useState(project.name);
  const [client, setClient] = useState("");
  const [versions, setVersions] = useState<Version[] | null>(null);
  const [savedPrint, setSavedPrint] = useState("");
  const [halted, setHalted] = useState(false);
  const locking = useRef(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const dirty = !!active && (fingerprint(project) !== savedPrint || active.name !== name || active.client !== client);
  const current = useRef({ active, name, client, user });
  current.current = { active, name, client, user };
  useEffect(() => { setName(project.name); }, [project.name]);
  useEffect(() => { onStaffChange?.(Boolean(user)); }, [user, onStaffChange]);
  useEffect(() => {
    if (takeOrdersLink()) { wantOrders.current = true; setVisible(true); }
    api<{user: User | null}>("/session").then(r => {
      setUser(r.user);
      const meta = r.user ? localMetadata(r.user) : null;
      if (meta) { setName(isLegacyAutoProjectName(meta.name) ? usePlannerStore.getState().project.name : meta.name); setClient(meta.client); }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  // По ссылке на заявки: раздел «Заявки», как только известен сотрудник с правом на них.
  useEffect(() => {
    if (!user || !wantOrders.current) return;
    wantOrders.current = false;
    if (can(user, "orders")) setSection("orders");
    else setNotice("Заявки открыты администратору и сотрудникам с правом «Заявки»");
  }, [user]);
  useEffect(() => {
    if (!user) return;
    try { localStorage.setItem(metadataKey, JSON.stringify({userId: user.id, name, client})); }
    catch { setError("Не удалось записать локальный черновик реквизитов. Не закрывайте страницу до сохранения на сервере."); }
  }, [user, name, client]);
  useEffect(() => {
    if (visible) { dialog.current?.showModal(); }
    else { dialog.current?.close(); }
  }, [visible]);
  // Сколько позиций прайса не задано или не проверено и сколько заявок ждут — в меню кабинета.
  useEffect(() => {
    if (!visible || !can(user, "prices")) return;
    api<AdminPrices>("/prices").then((prices) => setPriceAttention(priceAttentionCount(prices))).catch(() => {});
  }, [visible, user]);
  useEffect(() => {
    if (!visible || !can(user, "orders")) return;
    api<{requests: OrderRequest[]}>("/orders").then((r) => setOrdersPending(r.requests.filter((item) => item.status === "pending").length)).catch(() => {});
  }, [visible, user]);
  // Обложка визуализатором (№ 29): кабинет уступает место визуализатору и
  // открывается снова, когда рендер выбран или визуализатор закрыли.
  const coverRequested = useCatalogCover(state => state.requested);
  const coverWasRequested = useRef(false);
  useEffect(() => {
    if (coverRequested) setVisible(false);
    else if (coverWasRequested.current) setVisible(true);
    coverWasRequested.current = coverRequested;
  }, [coverRequested]);
  useEffect(() => {
    onStatus(!active ? "Сохранено локально" : halted ? "Не сохранено на сервере" : busy ? "Сохраняю…" : dirty ? "Есть изменения · локально" : "Сохранено в кабинете");
  }, [active, halted, busy, dirty, onStatus]);
  useEffect(() => {
    const unsaved = dirty || (section === "site" && siteDirty);
    const warn = (event: BeforeUnloadEvent) => { if (unsaved || locking.current) { event.preventDefault(); event.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty, section, siteDirty]);
  const refresh = async () => { const r = await api<{projects: Card[]}>("/projects"); setCards(r.projects); };
  const refreshTrash = async () => setTrash((await api<{projects: Card[]}>("/projects?trash=1")).projects);
  const problem = (e: unknown) => {
    setError(e instanceof Error ? e.message : "Не удалось выполнить действие");
    if (e instanceof ApiError && e.status === 401) setUser(null);
  };
  const run = async (action: () => Promise<void>) => {
    if (locking.current) return;
    locking.current = true; setBusy(true); setError(""); setNotice("");
    try { await action(); } catch (e) { problem(e); } finally { locking.current = false; setBusy(false); }
  };
  const previewTaken = useRef<{ id: string; at: number } | null>(null);
  const save = async (copy = false) => {
    if (locking.current || !current.current.user) return;
    locking.current = true; setBusy(true); setError("");
    const info = current.current;
    const snapshot = documentOf(usePlannerStore.getState().project);
    // Источник названия — конструктор; старое автоназвание из кабинета его не перебивает (11.10, start-03).
    const typed = info.name.trim();
    const title = (typed && !isLegacyAutoProjectName(typed) ? typed : snapshot.name).slice(0, 60);
    snapshot.name = title;
    try {
      // Картинка для карточки — из 3D; не вышло — на сервере остаётся прежняя.
      // При автосохранении — не чаще раза в 45 секунд: снимок занимает кадр.
      const targetId = info.active && !copy ? info.active.id : "";
      const fresh = previewTaken.current && previewTaken.current.id === targetId && Date.now() - previewTaken.current.at < 45_000;
      const preview = fresh ? undefined : (() => { try { return getPreview?.() ?? undefined; } catch { return undefined; } })();
      const r = await api<{project: Saved}>(info.active && !copy ? `/projects/${info.active.id}` : "/projects", info.active && !copy ? "PUT" : "POST", {
        name: title, client: info.client, document: snapshot, revision: info.active?.revision, ...(preview ? { preview } : {})
      });
      // Do not replace the editor with the response: the user may have edited it while saving.
      if (usePlannerStore.getState().project.name !== title && info.name === current.current.name) usePlannerStore.getState().setProjectName(title);
      if (info.name === current.current.name) setName(title);
      if (preview) previewTaken.current = { id: r.project.id, at: Date.now() };
      setActive(r.project); setSavedPrint(fingerprint(snapshot)); setHalted(false);
      setNotice(copy ? "Создана отдельная копия проекта" : "Проект сохранён на сервере");
      await refresh();
    } catch (e) { setHalted(true); problem(e); }
    finally { locking.current = false; setBusy(false); }
  };
  useEffect(() => {
    if (!user || !active || !dirty || busy || halted) return;
    const timer = window.setTimeout(() => void save(), 2500);
    return () => window.clearTimeout(timer);
  }, [project, name, client, active, user, dirty, busy, halted]);
  const preserveDraft = () => {
    if (usePlannerStore.getState().project.modules.length && !window.confirm("Открыть другой проект? Текущий черновик останется в резервной копии в этом браузере. Несохранённые серверные правки автоматически не перезаписываются.")) return false;
    localStorage.setItem(backupKey, JSON.stringify({document: usePlannerStore.getState().project, name: current.current.name, client: current.current.client}));
    return true;
  };
  const load = (p: Saved | null, doc: PlannerProject) => {
    usePlannerStore.getState().loadProject(doc);
    // Название — как в конструкторе после загрузки: старые автоназвания там уже «Новый проект».
    setActive(p); setName(usePlannerStore.getState().project.name); setClient(p?.client ?? "");
    setSavedPrint(fingerprint(usePlannerStore.getState().project)); setHalted(false); setVersions(null);
    setVisible(false);
    onOpen();
  };
  const openProject = (id: string) => run(async () => {
    const {project: p} = await api<{project: Saved}>(`/projects/${id}`);
    if (preserveDraft()) {
      setPublisherOpen(false); load(p, p.document);
      // У старого проекта нет картинки — снять её, когда сцена построится (10.10).
      if (!cards.find((card) => card.id === id)?.hasPreview) window.setTimeout(() => {
        const preview = (() => { try { return getPreview?.() ?? null; } catch { return null; } })();
        if (!preview || current.current.active?.id !== id) return;
        api(`/projects/${id}/preview`, "PUT", { preview })
          .then(() => { previewTaken.current = { id, at: Date.now() }; return refresh(); })
          .catch(() => undefined);
      }, 4000);
    }
  });
  /** В корзину: проект пропадает из списка, восстановить можно в «Корзине». */
  const trashProject = (card: Card) => run(async () => {
    if (!window.confirm(`Переместить «${card.name}» в корзину? Его можно будет восстановить. Модели этого проекта на витрине останутся.`)) return;
    await api(`/projects/${card.id}/trash`, "POST", {});
    if (current.current.active?.id === card.id) {
      // Конструкция остаётся в конструкторе — как локальный черновик.
      setActive(null); setSavedPrint(""); setHalted(false);
    }
    await refresh(); if (trash) await refreshTrash();
    setNotice(`«${card.name}» в корзине`);
  });
  const restoreProject = (card: Card) => run(async () => {
    await api(`/projects/${card.id}/restore`, "POST", {});
    await refresh(); await refreshTrash();
    setNotice(`«${card.name}» восстановлен`);
  });
  const purgeProject = (card: Card) => run(async () => {
    if (!window.confirm(`Удалить «${card.name}» навсегда вместе со всеми версиями? Это нельзя отменить.`)) return;
    await api(`/projects/${card.id}/purge`, "POST", {});
    await refreshTrash();
    setNotice(`«${card.name}» удалён навсегда`);
  });
  /**
   * Править модель витрины: открыть её проект кабинета, а если его ещё нет —
   * создать из модели и связать с ней. Обновляется она потом публикацией.
   */
  const editCatalogModel = (card: AdminCatalogModel) => run(async () => {
    const path = `/admin/catalog/${encodeURIComponent(card.id)}`;
    if (card.project) {
      // Проект дизайнера без права «Все проекты» не открыть — тогда правка
      // идёт в новом проекте кабинета, и модель переходит к нему (ниже).
      const linked = await (async () => {
        try {
          if (card.project!.deleted) await api(`/projects/${card.project!.id}/restore`, "POST", {});
          return (await api<{project: Saved}>(`/projects/${card.project!.id}`)).project;
        } catch (e) {
          if (e instanceof ApiError && e.status === 404) return null;
          throw e;
        }
      })();
      if (linked) {
        if (preserveDraft()) { setSection("projects"); setPublisherOpen(true); load(linked, linked.document); }
        return;
      }
    }
    if (!preserveDraft()) return;
    const {model} = await api<{model: AdminCatalogDetail}>(path);
    const base = {...structuredClone(initialProject), name: `Витрина · ${model.name}`.slice(0, 60)} as PlannerProject;
    const document = model.modules
      ? replaceWithCatalogComposition(base, model.modules)
      : replaceWithCatalogShelf(base, isChestShelf(model.module, model.category) ? {...model.module, shelvingKind: "chest"} : model.module);
    const moduleId = model.modules ? "wardrobe" : document.modules.find(module => module.type === "shelving")?.id;
    if (!moduleId) throw new Error("В модели нет изделия для витрины");
    const {project: saved} = await api<{project: Saved}>("/projects", "POST", {name: document.name, client: "", document: documentOf(document)});
    await api(`${path}/link`, "POST", {projectId: saved.id, moduleId});
    await refresh();
    setSection("projects"); setPublisherOpen(true);
    load(saved, saved.document);
  });
  const login = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); const form = event.currentTarget; const data = new FormData(form);
    void run(async () => {
      const r = await api<{user: User}>("/login", "POST", {login: data.get("login"), password: data.get("password")});
      const meta = localMetadata(r.user);
      setUser(r.user); setName(meta?.name && !isLegacyAutoProjectName(meta.name) ? meta.name : usePlannerStore.getState().project.name); setClient(meta?.client ?? "");
      setActive(null); setHalted(false); form.reset(); await refresh();
    });
  };
  const createUser = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); const form = event.currentTarget; const data = new FormData(form);
    void run(async () => {
      await api("/users", "POST", {name: data.get("name"), login: data.get("login"), password: data.get("password"),
        admin: data.get("admin") === "on", permissions: PERMISSIONS.map(([key]) => key).filter((key) => data.get(`perm-${key}`) === "on")});
      form.reset(); setUsers((await api<{users: User[]}>("/users")).users); setNotice("Сотрудник добавлен. Передайте ему логин и пароль лично.");
    });
  };
  /** Галочки прав и доступ сотрудника: сохраняются сразу. */
  const changeAccess = (target: User, change: { active?: boolean; admin?: boolean; permissions?: Permission[] }, message: string) => run(async () => {
    const r = await api<{user: User}>(`/users/${target.id}`, "PATCH", change);
    setUsers((list) => list.map((item) => item.id === target.id ? r.user : item));
    if (target.id === user?.id) setUser(r.user);
    setNotice(message);
  });
  /** Новый пароль сотрудника (11.10): если забыл. Его входы завершаются. */
  const [passwordFor, setPasswordFor] = useState<string | null>(null);
  const setPassword = (event: FormEvent<HTMLFormElement>, target: User) => {
    event.preventDefault(); const form = event.currentTarget; const password = String(new FormData(form).get("password") ?? "");
    void run(async () => {
      await api(`/users/${target.id}/password`, "PUT", { password });
      form.reset(); setPasswordFor(null);
      setNotice(target.id === user?.id ? "Пароль изменён. Войдите заново с новым паролем." : `${target.name}: пароль изменён. Передайте его лично.`);
      if (target.id === user?.id) setUser(null);
    });
  };
  /** Несохранённые цены и тексты сайта не теряются молча при уходе из раздела. */
  const confirmLeavePrices = () =>
    (section !== "prices" || !pricesDirty || window.confirm("Есть несохранённые изменения цен. Уйти без сохранения?"))
    && (section !== "site" || !siteDirty || window.confirm("Есть несохранённые изменения сайта. Уйти без сохранения?"));
  const close = () => {
    if (!confirmLeavePrices()) return;
    setVisible(false); trigger.current?.focus();
  };
  const openSection = (next: Section) => {
    if (next === section || !confirmLeavePrices()) return;
    if (next === "prices") setPricesDirty(false);
    if (next === "site") setSiteDirty(false);
    if (next === "staff") void run(async () => { setUsers((await api<{users: User[]}>("/users")).users); setSection("staff"); });
    else setSection(next);
    if (next !== "projects") setTrash(null);
  };
  return <>
    <button ref={trigger} className="workspace-trigger" type="button" onClick={() => {
      setVisible(true);
      if (user) void run(refresh);
    }}>{user ? "Мои проекты" : "Кабинет"}</button>
    <dialog ref={dialog} className={`workspace-dialog${user ? " is-admin" : ""}`} aria-busy={busy} onCancel={(event) => { event.preventDefault(); close(); }} onClose={() => setVisible(false)} aria-labelledby="workspace-heading">
      {loading || !user ? <>
        <header className="workspace-heading"><div><span>EGEL · РАБОЧЕЕ ПРОСТРАНСТВО</span><h2 id="workspace-heading">Кабинет сотрудников</h2><p>Ваши проекты — на любом рабочем компьютере</p></div><button type="button" onClick={close} aria-label="Закрыть кабинет">✕</button></header>
        {error && <div className="workspace-error" role="alert">{error}</div>}
        {notice && <div className="workspace-notice" role="status">{notice}</div>}
        {loading ? <p>Проверяю вход…</p> : <form className="workspace-login" onSubmit={login}>
          <label>Логин<input name="login" autoComplete="username" required maxLength={64} /></label>
          <label>Пароль<input name="password" type="password" autoComplete="current-password" required maxLength={128} /></label>
          <button className="workspace-primary" disabled={busy}>Войти</button>
          <p>Доступ выдаёт администратор. Открытой регистрации нет. Конфигуратор можно использовать и без входа — с локальным сохранением.</p>
        </form>}
      </> : <div className="ws-shell">
        <aside className="ws-sidebar">
          <div className="ws-brand"><span className="ws-brand__mark">E</span><div><strong>Egel Design</strong><small>Кабинет</small></div></div>
          <nav className="ws-nav" aria-label="Разделы кабинета">
            <button type="button" aria-current={section === "projects" ? "page" : undefined} onClick={() => openSection("projects")}>
              <span>Проекты</span><small>{cards.length}</small></button>
            {can(user, "orders") && <button type="button" aria-current={section === "orders" ? "page" : undefined} disabled={busy} onClick={() => openSection("orders")}>
              <span>Заявки</span>{ordersPending > 0 && <small className="is-attention" title="Ожидают проверки">{ordersPending}</small>}</button>}
            {can(user, "catalog") && <button type="button" aria-current={section === "catalog" ? "page" : undefined} disabled={busy} onClick={() => openSection("catalog")}>
              <span>Витрина</span>{catalogCount > 0 && <small>{catalogCount}</small>}</button>}
            {can(user, "prices") && <button type="button" aria-current={section === "prices" ? "page" : undefined} disabled={busy} onClick={() => openSection("prices")}>
              <span>Цены</span>{priceAttention > 0 && <small className="is-attention" title="Не заданы или не проверены">{priceAttention}</small>}</button>}
            {can(user, "site") && <button type="button" aria-current={section === "site" ? "page" : undefined} disabled={busy} onClick={() => openSection("site")}>
              <span>Сайт</span></button>}
            {user.role === "admin" && <button type="button" aria-current={section === "staff" ? "page" : undefined} disabled={busy} onClick={() => openSection("staff")}>
              <span>Сотрудники</span>{users.length > 0 && <small>{users.length}</small>}</button>}
          </nav>
          <div className="ws-sidebar__foot">
            <div className="ws-user"><strong>{user.name}</strong><small>{accessLabel(user)}</small></div>
            <button type="button" onClick={close}>← В конструктор</button>
            <button type="button" disabled={busy} onClick={() => void run(async () => {
              if (!confirmLeavePrices()) return;
              if (dirty && !window.confirm("Выйти без сохранения на сервер? Текущая конструкция останется локально на этом компьютере.")) return;
              await api("/logout", "POST", {}); setUser(null); setActive(null); setCards([]); setTrash(null); setUsers([]); setHalted(false); setSection("projects"); setPricesDirty(false); setSiteDirty(false);
            })}>Выйти</button>
          </div>
        </aside>
        <main className="ws-main">
          <header className="ws-page-head">
            <div><span className="workspace-eyebrow">EGEL · КАБИНЕТ</span><h2 id="workspace-heading">{SECTION_TITLES[section].title}</h2><p>{section === "projects" && !can(user, "projects") ? "Ваши проекты и текущая конструкция" : SECTION_TITLES[section].lead}</p></div>
            <button type="button" className="ws-close" onClick={close} aria-label="Закрыть кабинет">✕</button>
          </header>
          {error && <div className="workspace-error" role="alert">{error}</div>}
          {notice && <div className="workspace-notice" role="status">{notice}</div>}
          {section === "projects" ? <>
          <section className="workspace-current"><div><span className="workspace-eyebrow">СЕЙЧАС В КОНСТРУКТОРЕ</span><h3>{active ? "Работа над проектом" : "Локальный черновик"}</h3><p>{active ? `Версия ${active.revision} · ${halted ? "автосохранение приостановлено" : dirty ? "ожидает сохранения" : "все изменения сохранены"}` : "Сохраните в кабинет, чтобы продолжить с другого компьютера"}</p></div>
            <div className="workspace-fields"><label>Название проекта<input value={name} maxLength={100} onChange={e => setName(e.target.value)} /></label><label>Клиент / номер заказа<input value={client} maxLength={160} placeholder="Необязательно" onChange={e => setClient(e.target.value)} /></label></div>
            <div className="workspace-actions"><button className="workspace-primary" disabled={busy || !name.trim()} onClick={() => void save()}>{busy ? "Подождите…" : active ? "Сохранить сейчас" : "Сохранить в кабинет"}</button>
              {active && <button disabled={busy} onClick={() => void save(true)}>Сохранить копию</button>}
              {active && <button disabled={busy} onClick={() => void run(async () => setVersions((await api<{versions: Version[]}>(`/projects/${active.id}/versions`)).versions))}>История версий</button>}
            </div>
            {active && can(user, "catalog") && <CatalogPublisher key={active.id} saved={active} disabled={busy || dirty || halted} defaultOpen={publisherOpen} />}
            {versions && <div className="workspace-versions"><p>Открытие старой версии создаст локальный черновик. Серверный оригинал останется на месте. Хранятся последние 50 версий.</p>{versions.map(v => <button key={v.revision} disabled={busy} onClick={() => void run(async () => {
              const r = await api<{version: Saved}>(`/projects/${active!.id}/versions/${v.revision}`);
              if (preserveDraft()) load(null, {...r.version.document, name: `${r.version.name} · версия ${v.revision}`});
            })}>Версия {v.revision} · {date(v.savedAt)} · {v.authorName}</button>)}</div>}
          </section>
          <div className="workspace-library-bar"><input aria-label="Поиск проектов" placeholder="Название, клиент или сотрудник…" value={search} onChange={e => setSearch(e.target.value)} /><button disabled={busy} onClick={() => void run(async () => { if (preserveDraft()) load(null, structuredClone(initialProject)); })}>+ Новый проект</button><button disabled={busy} onClick={() => void run(async () => {
            const previous = localStorage.getItem(backupKey); if (!previous) { setNotice("Резервного черновика пока нет"); return; }
            const backup = JSON.parse(previous);
            const doc = (backup.document ?? backup) as PlannerProject;
            if (preserveDraft()) { load(null, {...doc, name: backup.name ?? doc.name}); setClient(backup.client ?? ""); }
          })}>Резервный черновик</button>
            <button aria-pressed={trash !== null} disabled={busy} onClick={() => void run(async () => { if (trash) setTrash(null); else await refreshTrash(); })}>
              {trash ? "← К проектам" : "Корзина"}</button></div>
          {trash ? <>
            <p className="workspace-muted">Корзина · {trash.length}. Проект из корзины можно восстановить{can(user, "projects") ? " или удалить навсегда вместе с версиями" : "; удалить навсегда может сотрудник с правом «Все проекты»"}.</p>
            <div className="workspace-grid">{trash.filter(c => `${c.name} ${c.client} ${c.ownerName}`.toLowerCase().includes(search.toLowerCase())).map(c => <article key={c.id} className="is-trashed">
              <ProjectCardImage card={c} /><h3>{c.name}</h3><p>{c.client || "Без клиента"}</p><small>В корзине с {date(c.deletedAt ?? c.updatedAt)} · {c.ownerName}</small>
              <button disabled={busy} onClick={() => void restoreProject(c)}>Восстановить</button>
              {can(user, "projects") && <button className="workspace-danger" disabled={busy} onClick={() => void purgeProject(c)}>Удалить навсегда</button>}
            </article>)}</div>
            {!trash.length && <div className="workspace-empty"><h3>Корзина пуста</h3><p>Удалённые проекты попадают сюда.</p></div>}
          </> : <>
          <p className="workspace-muted">{can(user, "projects") ? "Проекты всех сотрудников" : "Ваши проекты"} · {cards.length}</p>
          <div className="workspace-grid">{cards.filter(c => `${c.name} ${c.client} ${c.ownerName}`.toLowerCase().includes(search.toLowerCase())).map(c => <article key={c.id} className={active?.id === c.id ? "is-current" : ""}>
            <ProjectCardImage card={c} /><h3>{c.name}</h3><p>{c.client || "Без клиента"}</p><small>{date(c.updatedAt)} · {c.ownerName}</small><button disabled={busy} onClick={() => void openProject(c.id)}>Открыть проект →</button>
            <button className="workspace-quiet" disabled={busy} onClick={() => void trashProject(c)}>В корзину</button>
          </article>)}</div>
          {!cards.length && <div className="workspace-empty"><h3>Здесь будут проекты клиентов</h3><p>Сохраните текущую конструкцию или начните новый проект.</p></div>}
          </>}
          </> : section === "orders" ? <OrdersAdmin busy={busy} run={run} notify={setNotice} onPending={setOrdersPending} />
          : section === "catalog" ? <CatalogAdmin busy={busy} run={run} notify={setNotice} onCount={setCatalogCount} onEdit={(model) => void editCatalogModel(model)} />
          : section === "prices" ? <PriceEditor busy={busy} run={run} notify={setNotice} onDirtyChange={setPricesDirty} onAttentionChange={setPriceAttention} />
          : section === "site" ? <SiteEditor busy={busy} run={run} notify={setNotice} onDirtyChange={setSiteDirty} />
          : <section className="workspace-staff"><h3>Добавить сотрудника</h3><form onSubmit={createUser}><label>Имя<input name="name" required maxLength={100} /></label><label>Логин<input name="login" required minLength={3} maxLength={64} autoComplete="off" /></label><label>Первый пароль<input name="password" type="password" required minLength={12} maxLength={128} autoComplete="new-password" placeholder="Не менее 12 символов" /></label>
            <fieldset className="workspace-rights"><legend>Права</legend>
              {PERMISSIONS.map(([key, title, hint]) => <label key={key}><input type="checkbox" name={`perm-${key}`} /><span>{title}<small>{hint}</small></span></label>)}
              <label><input type="checkbox" name="admin" /><span>Администратор<small>всё, включая сотрудников и их права</small></span></label>
            </fieldset>
            <button className="workspace-primary" disabled={busy}>Добавить сотрудника</button></form>
          <p>Свои проекты, корзина своих проектов и визуализации без лимита есть у каждого. Остальное — по галочкам; изменения действуют сразу, без повторного входа. При отключении сотрудника его проекты сохраняются, а активные сессии закрываются.</p>
          {users.map(u => <div className={`workspace-user${u.active ? "" : " is-off"}`} key={u.id}>
            <div className="workspace-user__head"><div><strong>{u.name}{u.id === user.id ? " · вы" : ""}</strong><small>{u.login} · {u.active ? accessLabel(u) : "Отключён"}</small></div>
              <span className="workspace-user__actions">{u.id !== user.id && <button disabled={busy} onClick={() => {
                if (window.confirm(`${u.active ? "Отключить" : "Включить"} доступ для ${u.name}?`)) void changeAccess(u, {active: !u.active}, `${u.name}: доступ ${u.active ? "отключён" : "включён"}`);
              }}>{u.active ? "Отключить" : "Включить"}</button>}
              <button disabled={busy} onClick={() => setPasswordFor(passwordFor === u.id ? null : u.id)}>Новый пароль</button></span></div>
            {passwordFor === u.id && <form className="workspace-password" onSubmit={(event) => setPassword(event, u)}>
              <input name="password" type="password" required minLength={12} maxLength={128} autoComplete="new-password"
                placeholder="Новый пароль, не менее 12 символов" aria-label={`Новый пароль: ${u.name}`} />
              <button className="workspace-primary" disabled={busy}>Сохранить пароль</button>
              <button type="button" onClick={() => setPasswordFor(null)}>Отмена</button>
            </form>}
            <div className="workspace-rights" role="group" aria-label={`Права: ${u.name}`}>
              {PERMISSIONS.map(([key, title, hint]) => <label key={key} title={hint}>
                <input type="checkbox" checked={u.role === "admin" || !!u.permissions?.includes(key)} disabled={busy || u.role === "admin"}
                  onChange={(event) => {
                    const next = PERMISSIONS.map(([k]) => k).filter((k) => k === key ? event.target.checked : !!u.permissions?.includes(k));
                    void changeAccess(u, {permissions: next}, `${u.name}: «${title}» ${event.target.checked ? "выдано" : "снято"}`);
                  }} /><span>{title}</span></label>)}
              <label title="Всё, включая сотрудников и их права"><input type="checkbox" checked={u.role === "admin"} disabled={busy || u.id === user.id}
                onChange={(event) => {
                  if (event.target.checked && !window.confirm(`Сделать ${u.name} администратором? Он получит все права, в том числе управление сотрудниками.`)) return;
                  void changeAccess(u, {admin: event.target.checked}, `${u.name}: ${event.target.checked ? "теперь администратор" : "больше не администратор — права по галочкам"}`);
                }} /><span>Администратор</span></label>
            </div>
          </div>)}
        </section>}
        </main>
      </div>}
    </dialog>
  </>;
}
